import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Spinner from '../Spinner';

const Button = React.forwardRef((props, ref) => {
  const {
    children,
    variant,
    block,
    icon,
    wrapperClass,
    iconClass,
    className,
    disabled,
    loading,
    active,
    ...rest
  } = props;
  const defaultClass = 'button rounded-lg';

  const disabledClass = 'opacity-50 cursor-not-allowed';
  const primary = () => {
    const btn = {
      bgColor: `bg-primary`,
      textColor: 'text-white',
      hoverColor: active ? '' : `hover:opacity-90 transition-all duration-300`,
      activeColor: `active:bg-primary`,
    };
    return getBtnColor(btn);
  };
  const secondary = () => {
    const btn = {
      bgColor: `bg-[#4CC900] `,
      textColor: 'text-white',
      hoverColor: active ? '' : `hover:opacity-90 transition-all duration-300`,
      activeColor: `active:bg-[#4CC900]`,
    };
    return getBtnColor(btn);
  };
  const outline = () => {
    const btn = {
      bgColor: active ? `bg-gray-100` : `bg-white border border-black `,
      textColor: 'text-darkblue',
      hoverColor: active ? '' : ``,
      activeColor: ``,
    };
    return getBtnColor(btn);
  };
  const plain = () => {
    const btn = {
      bgColor: active ? `bg-gray-200` : 'bg-[#F2F2F2]',
      textColor: `text-darkblue `,
      hoverColor: active ? '' : `hover:bg-[#F2F2F295]`,
      activeColor: `active:bg-gray-100 `,
    };
    return getBtnColor(btn);
  };
  const ghost = () => {
    const btn = {
      bgColor: active
        ? `bg-gray-100`
        : 'bg-transparent border border-transparent',
      textColor: `text-darkblue `,
      hoverColor: active ? '' : `hover:bg-gray-50 `,
      activeColor: `active:bg-gray-100 `,
      type: 'ghost',
    };
    return getBtnColor(btn);
  };
  const defaultColor = () => {
    const btn = {
      bgColor: active
        ? `bg-gray-100 border border-gray-300`
        : `bg-white border border-gray-300`,
      textColor: `text-gray-600 `,
      hoverColor: active ? '' : `hover:bg-gray-50 `,
      activeColor: `active:bg-gray-100 `,
    };
    return getBtnColor(btn);
  };
  const getBtnColor = ({
    bgColor,
    hoverColor,
    activeColor,
    textColor,
    type,
  }) => {
    return `${bgColor} ${
      disabled || loading
        ? classNames(
            disabledClass,
            type === 'ghost'
              ? '!text-[#808080] !bg-[#E1E1E1] !opacity-100 font-medium'
              : ''
          )
        : hoverColor + ' ' + activeColor
    } ${textColor}`;
  };

  const btnColor = () => {
    switch (variant) {
      case 'primary':
        return primary();
      case 'secondary':
        return secondary();
      case 'outline':
        return outline();
      case 'plain':
        return plain();
      case 'ghost':
        return ghost();
      case 'default':
        return defaultColor();
      default:
        return primary();
    }
  };

  const classes = classNames(
    defaultClass,
    btnColor(),
    className,
    block ? 'w-full' : ''
  );

  const handleClick = (e) => {
    const { onClick } = props;
    if (disabled || loading) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  const renderChildren = () => {
    if (loading && children) {
      return (
        <span className="flex items-center justify-center">
          <Spinner className="mr-1 text-white" />
          {children}
        </span>
      );
    }
    if (icon && !children && loading) {
      return <Spinner className="text-text-green-800 text-white" />;
    }
    if (icon && !children && !loading) {
      return <>{icon}</>;
    }
    if (icon && children && !loading) {
      return (
        <span
          className={classNames(
            wrapperClass || 'flex items-center justify-center'
          )}
        >
          <span className={classNames(iconClass || 'text-lg')}>{icon}</span>
          <span className="ml-1 text-center flex-1">{children}</span>
        </span>
      );
    }
    return <>{children}</>;
  };

  return (
    <button ref={ref} className={classes} {...rest} onClick={handleClick}>
      {renderChildren()}
    </button>
  );
});

Button.propTypes = {
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  block: PropTypes.bool,
  className: PropTypes.string,
  iconClass: PropTypes.string,
  wrapperClass: PropTypes.string,
  variant: PropTypes.oneOf([
    'primary',
    'site',
    'secondary',
    'outline',
    'plain',
    'ghost',
    'default',
  ]),
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  active: PropTypes.bool,
};

Button.defaultProps = {
  variant: 'default',
  active: false,
  loading: false,
  disabled: false,
};

export default Button;
